<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { authErrorMessage, useAuth } from '../composables/useAuth'
import BaseField from '../ui/components/BaseField.vue'

const emit = defineEmits<{ forgotPassword: [email: string] }>()

const { login } = useAuth()
const router = useRouter()
const route  = useRoute()

const email    = ref('')
const password = ref('')
const busy     = ref(false)
const error    = ref<string | null>(
  route.query.error === 'account-inactive'
    ? 'This account is not active. Contact your administrator.'
    : null,
)

async function onSubmit() {
  if (busy.value) return
  error.value = null
  busy.value = true
  try {
    await login(email.value.trim(), password.value)

    // Only accept same-origin relative paths. An open redirect here would let a
    // crafted link bounce a freshly authenticated user to another site.
    const redirect = route.query.redirect
    const target =
      typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        : '/app'
    await router.replace(target)
  } catch (e) {
    const code = (e as { code?: string }).code ?? ''
    error.value =
      code === 'app/account-inactive'
        ? 'This account is not active. Contact your administrator.'
        : authErrorMessage(code)
    password.value = ''
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="login" @submit.prevent="onSubmit">
    <BaseField label="Email">
      <template #default="{ id }">
        <input
          :id="id"
          v-model="email"
          type="email"
          autocomplete="username"
          required
          :disabled="busy"
          class="input"
        />
      </template>
    </BaseField>

    <BaseField label="Password">
      <template #default="{ id }">
        <input
          :id="id"
          v-model="password"
          type="password"
          autocomplete="current-password"
          required
          :disabled="busy"
          class="input"
        />
      </template>
    </BaseField>

    <p v-if="error" class="field-error" role="alert">{{ error }}</p>

    <button type="submit" class="btn btn-primary" :disabled="busy">
      {{ busy ? 'Signing in…' : 'Sign in' }}
    </button>

    <button
      type="button"
      class="btn btn-link forgot"
      :disabled="busy"
      @click="emit('forgotPassword', email.trim())"
    >
      Forgot password?
    </button>
  </form>
</template>

<style scoped>
.login {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

/* Centre-align the "Forgot password?" link and add a small text-decoration
   flourish — the base .btn-link is left-aligned inline by default. */
.forgot {
  align-self: center;
  text-decoration: underline;
}
</style>
